// src/components/InitialQuestions/InitialQuestionsComponent.tsx
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Send } from 'lucide-react';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
import { detectRoleStrict, type Role } from '../../lib/role';
import outputs from '../../../amplify_outputs.json';

type Issue = Schema['Issue']['type'];

const agreePath = (id: string) => `/session/${encodeURIComponent(id)}/agree`;
const ENQUEUE_API_URL: string = (outputs as any)?.custom?.proposeIssuesApiUrl || '';
const POLL_INTERVAL = 3000; // Poll every 3 seconds

async function fetchIssue(issueId: string): Promise<Issue | null> {
  const resp = await client.models.Issue.get({ id: issueId }, { authMode: 'userPool' });
  return (resp as any)?.data ?? null;
}

function Bubble({ from, children }: { from: 'system' | 'user'; children: React.ReactNode }) {
  const isUser = from === 'user';
  return (
    <div
      className={`d-flex ${isUser ? 'justify-content-end' : 'justify-content-start'} mb-2`}
      style={{ width: '100%' }}
    >
      <div
        className={`px-3 py-2 rounded-4 ${
          isUser ? 'bg-primary text-white' : 'bg-light border'
        }`}
        style={{ maxWidth: 680, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}
      >
        {children}
      </div>
    </div>
  );
}

function toStrArr(arr?: (string | null)[] | null): string[] {
  if (!Array.isArray(arr)) return [];
  // Keep entries (including empty) so length reflects "answered or skipped"
  return arr.map((x) => (typeof x === 'string' ? x.trim() : '')).filter(() => true);
}

function readQuestionsForRole(rec: Partial<Issue> | null | undefined, role: Role): string[] {
  if (!rec || !role) return [];
  return role === 'initiator'
    ? toStrArr((rec as any).inferredQuestionsInitiator)
    : toStrArr((rec as any).inferredQuestionsCollaborator);
}

function len(x: unknown) {
  return Array.isArray(x) ? x.length : 0;
}

function bothSidesAnswered(rec: Partial<Issue> | null | undefined): boolean {
  if (!rec) return false;
  const qi = len((rec as any).inferredQuestionsInitiator);
  const qc = len((rec as any).inferredQuestionsCollaborator);
  const ai = len((rec as any).initiatorQuestionAnswers);
  const ac = len((rec as any).collaboratorQuestionAnswers);
  if (qi === 0 || qc === 0) return false;
  return ai >= qi && ac >= qc;
}

function roleCompleted(rec: Partial<Issue> | null | undefined, role: Role): boolean {
  if (!rec || !role) return false;
  const q =
    role === 'initiator'
      ? len((rec as any).inferredQuestionsInitiator)
      : len((rec as any).inferredQuestionsCollaborator);
  const a =
    role === 'initiator'
      ? len((rec as any).initiatorQuestionAnswers)
      : len((rec as any).collaboratorQuestionAnswers);
  if (q === 0) return false;
  return a >= q;
}

function hasProposedIssues(rec: Partial<Issue> | null | undefined): boolean {
  if (!rec) return false;
  const any =
    !!(rec as any).proposedIssueCandidatesJson ||
    !!(rec as any).proposedIssueCandidatesJs ||
    (Array.isArray((rec as any).proposedIssues) &&
      (rec as any).proposedIssues.length > 0) ||
    (Array.isArray((rec as any).proposedIssueCandidates) &&
      (rec as any).proposedIssueCandidates.length > 0) ||
    (Array.isArray((rec as any).reframedIssueCandidates) &&
      (rec as any).reframedIssueCandidates.length > 0);
  return !!any;
}

function noInferredQuestions(rec: Partial<Issue> | null | undefined): boolean {
  if (!rec) return false;
  const qi = len((rec as any).inferredQuestionsInitiator);
  const qc = len((rec as any).inferredQuestionsCollaborator);
  return qi === 0 && qc === 0;
}

export default function InitialQuestionsComponent() {
  const navigate = useNavigate();
  const { id: issueIdParam } = useParams();
  const issueId = issueIdParam ?? '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roleWarning, setRoleWarning] = useState<string | null>(null);
  const [issue, setIssue] = useState<Issue | null>(null);
  const [role, setRole] = useState<Role>(null);
  const [, setUserEmail] = useState<string | null>(null);
  const [questions, setQuestions] = useState<string[]>([]);
  const [answers, setAnswers] = useState<string[]>([]);
  const [idx, setIdx] = useState(0);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const didNavigateRef = useRef(false);
  const proposeEnqueuedRef = useRef(false);

  // Initial load
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        setRoleWarning(null);

        if (!issueId) throw new Error('Missing issue id.');
        const data = await fetchIssue(issueId);
        if (!data) throw new Error('Issue not found.');

        let email: string | null = null;
        try {
          await getCurrentUser();
          const attrs = await fetchUserAttributes();
          email = attrs?.email ?? null;
        } catch {
          email = null;
        }
        setUserEmail(email);

        const r = detectRoleStrict(email, data);
        if (!r) {
          setRole(null);
          setRoleWarning('Role not verified. Sign in with the invited email.');
          setIssue(data as Issue);
          setQuestions([]);
          setAnswers([]);
          setIdx(0);
          return;
        }

        const qs = readQuestionsForRole(data, r);
        const existingAnswers =
          r === 'initiator'
            ? toStrArr((data as any).initiatorQuestionAnswers).slice(0, qs.length)
            : toStrArr((data as any).collaboratorQuestionAnswers).slice(0, qs.length);

        if (!mounted) return;

        setIssue(data as Issue);
        setRole(r);
        setQuestions(qs);
        setAnswers(
          existingAnswers.length === qs.length ? existingAnswers : Array(qs.length).fill('')
        );

        const firstUnanswered = existingAnswers.findIndex((a) => !a || !a.trim());
        setIdx(
          firstUnanswered === -1
            ? existingAnswers.length
            : Math.max(0, firstUnanswered)
        );

        if (roleCompleted(data, r) && !didNavigateRef.current) {
          didNavigateRef.current = true;
          navigate(agreePath(data.id));
        }
      } catch (e: any) {
        if (mounted) setError(e?.message ?? String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [issueId, navigate]);

  // Polling effect - replaces observeQuery
  useEffect(() => {
    if (!issue?.id || !role || loading || error) return;

    let adopted = false;
    let intervalId: NodeJS.Timeout;

    const poll = async () => {
      try {
        const rec = await fetchIssue(issue.id);
        if (!rec) return;

        setIssue(rec);

        if (!adopted) {
          const nextQs = readQuestionsForRole(rec, role);
          if (nextQs.length > 0) {
            const answersFromRec =
              role === 'collaborator'
                ? toStrArr((rec as any)?.collaboratorQuestionAnswers).slice(
                    0,
                    nextQs.length
                  )
                : toStrArr((rec as any)?.initiatorQuestionAnswers).slice(
                    0,
                    nextQs.length
                  );

            setQuestions(nextQs);
            setAnswers(
              answersFromRec.length === nextQs.length
                ? answersFromRec
                : Array(nextQs.length).fill('')
            );

            const firstUn = answersFromRec.findIndex((a) => !a || !a.trim());
            setIdx(
              firstUn === -1
                ? answersFromRec.length
                : Math.max(0, firstUn)
            );
            adopted = true;
          }
        }
      } catch {
        // Silent polling failure
      }
    };

    // Poll immediately, then set up interval
    poll();
    intervalId = setInterval(poll, POLL_INTERVAL);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [issue?.id, role, loading, error]);

  // Navigation effect
  useEffect(() => {
    if (!issue || !role || loading || error || didNavigateRef.current) return;
    const currentDone = roleCompleted(issue, role);
    const allDone = bothSidesAnswered(issue);
    if (currentDone || allDone) {
      didNavigateRef.current = true;
      navigate(agreePath(issue.id));
    }
  }, [issue, role, loading, error, navigate]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [idx, loading]);

  async function handleFinishAll() {
    if (!issue) return;
    if (!role) {
      setError('Role not verified. Use the invited email.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const patch: any = { id: issue.id };

      if (role === 'initiator') {
        patch.initiatorQuestionAnswers = answers.slice(0, questions.length);
      } else {
        patch.collaboratorQuestionAnswers = answers.slice(0, questions.length);
      }

      const { data, errors } = await client.models.Issue.update(patch, {
        authMode: 'userPool',
      });

      if (errors?.length) {
        throw new Error(errors.map((e) => e.message).join('; '));
      }
      if (!data) {
        throw new Error('Update failed.');
      }

      const fresh = await fetchIssue(data.id);

      const everyoneDone = bothSidesAnswered(fresh);
      const noQs = noInferredQuestions(fresh);
      const proposalsExist = hasProposedIssues(fresh);

      const shouldEnqueue =
        !!role &&
        !proposalsExist &&
        !proposeEnqueuedRef.current &&
        (everyoneDone || noQs);

      if (shouldEnqueue) {
        if (!ENQUEUE_API_URL) {
          throw new Error('Missing enqueue API URL.');
        }
        proposeEnqueuedRef.current = true;
        await fetch(ENQUEUE_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ issueId: data.id }),
        });
      }

      setIssue(fresh);

      if (!didNavigateRef.current) {
        didNavigateRef.current = true;
        navigate(agreePath(data.id));
      }
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  }

  function handleSkip() {
    if (idx >= questions.length) return;
    const next = answers.slice();
    next[idx] = ''; // treated as answered/visited
    setAnswers(next);
    setDraft('');
    if (idx < questions.length - 1) setIdx(idx + 1);
    else void handleFinishAll();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (idx >= questions.length || !questions[idx]) return;
    const next = answers.slice();
    next[idx] = draft.trim();
    setAnswers(next);
    setDraft('');
    if (idx < questions.length - 1) setIdx(idx + 1);
    else void handleFinishAll();
  }

  const roleTitle =
    role === 'collaborator'
      ? `Three Questions for ${issue?.collaboratorFirstName}`
      : role === 'initiator'
      ? `Three Questions for ${issue?.initiatorFirstName}`
      : 'Questions';

  const waitingForQuestions =
    !loading && !error && role && questions.length === 0;

  return (
    <Container
      className="py-4 page-gradient d-flex align-items-start justify-content-center"
      style={{ minHeight: '100vh' }}
    >
      <div style={{ width: '100%', maxWidth: 900 }}>
        <div className="mb-3 d-flex align-items-center gap-2" />

        <Card
          className="shadow-sm border-0 rounded-4 mx-auto"
          style={{ maxWidth: 900 }}
        >
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h3 className="m-0">{roleTitle}</h3>
            </div>

            {roleWarning && (
              <Alert variant="warning" className="mb-3">
                {roleWarning}{' '}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="ms-2"
                  onClick={() => navigate('/secure-access')}
                >
                  Sign in
                </Button>
              </Alert>
            )}

            {loading && (
              <div className="d-flex align-items-center gap-2">
                <Spinner size="sm" />
                <span>Loading…</span>
              </div>
            )}

            {!!error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {waitingForQuestions && (
              <Alert variant="info" className="mb-2">
                <div className="d-flex align-items-center gap-2">
                  <Spinner size="sm" />
                  <div>
                    <strong>Waiting for questions...</strong>
                  </div>
                </div>
              </Alert>
            )}

            {!loading && !error && role && questions.length > 0 && (
              <>
                <div
                  className="mb-3"
                  style={{
                    maxHeight: 420,
                    overflowY: 'auto',
                    borderRadius: 16,
                    border: '1px solid var(--bs-border-color)',
                    padding: 12,
                    background: 'var(--bs-body-bg)',
                  }}
                >
                  {questions
                    .slice(0, Math.min(idx, questions.length))
                    .map((q, i) => (
                      <div key={`past-${i}`}>
                        <Bubble from="system">{q}</Bubble>
                        {answers[i] !== undefined && answers[i] !== '' && (
                          <Bubble from="user">{answers[i]}</Bubble>
                        )}
                        {answers[i] === '' && (
                          <Bubble from="user">
                            <em className="text-muted">Skipped</em>
                          </Bubble>
                        )}
                      </div>
                    ))}
                  {idx < questions.length && questions[idx] && (
                    <Bubble from="system">{questions[idx]}</Bubble>
                  )}
                </div>

                {idx < questions.length && (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group controlId="answer">
                      <Form.Label className="fw-semibold">
                        Your answer
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        ref={inputRef}
                        rows={3}
                        placeholder="Type your response…"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        disabled={saving}
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted">
                        Question {Math.min(idx + 1, questions.length)} of{' '}
                        {questions.length}
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          type="button"
                          onClick={handleSkip}
                          disabled={saving}
                        >
                          Skip
                        </Button>
                        <Button
                          variant="primary"
                          type="submit"
                          disabled={saving}
                        >
                          <Send size={16} className="me-1" />
                          Send
                        </Button>
                      </div>
                    </div>
                  </Form>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
}
